import { useEffect, useState, useMemo, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useFavorites } from "@/features/favorites/use-favorites";
import { useMarkets } from "@/solana/hooks/use-markets";
import { useProduct } from "@/features/product/product-context";
import { useProductOptions } from "@/features/product/product-options";
import { useMarketPrecision } from "@/hooks/use-price-precision";
import { cn } from "@/lib/utils";
import { useMarketPrice } from "@/backend/store";
import type { Market } from "@/solana/programs/types";

// Individual market button with real-time price subscription
const FavoriteMarketButton = memo(function FavoriteMarketButton({
  market,
  isActive,
  onClick,
}: {
  market: Market;
  isActive: boolean;
  onClick: () => void;
}) {
  // Subscribe to real-time price from Zustand store
  const livePrice = useMarketPrice(market.symbol);
  // Get market precision dynamically from backend config
  const { quotePrecision } = useMarketPrecision(market.symbol);
  const priceChange = market.priceChangePercent24h || 0;
  const isPositive = priceChange >= 0;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 transition-colors flex-shrink-0 group"
    >
      <span className={cn(
        "transition-colors text-[11px]",
        isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
      )}>
        {market.symbol}
      </span>
      <span className={cn(
        "transition-colors",
        isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
      )}>
        ${livePrice?.toLocaleString(undefined, {
          minimumFractionDigits: quotePrecision,
          maximumFractionDigits: quotePrecision,
        }) || "—"}
      </span>
      <span className={cn(
        "transition-colors",
        isPositive ? "text-buy/70 group-hover:text-buy" : "text-sell/70 group-hover:text-sell"
      )}>
        {isPositive ? "+" : ""}{priceChange.toFixed(2)}%
      </span>
    </button>
  );
});

export const StatusBar = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { favorites } = useFavorites();
  const { markets } = useMarkets();
  const { product, setProduct } = useProduct();
  const { products } = useProductOptions();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const favoriteMarkets = useMemo(() => {
    if (!markets || favorites.length === 0) return [];
    return markets.filter(m => favorites.includes(m.symbol));
  }, [markets, favorites]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleMarketClick = (symbol: string) => {
    const prod = products?.find(p => p.id === symbol);
    if (prod) {
      setProduct(prod);
      navigate(`/trade/${prod.id}`, { replace: true });
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex h-8 items-center justify-between border-t bg-background px-4 text-xs">
      {/* Left Side - Online Status + Favorites */}
      <div className="flex items-center gap-3 flex-1 min-w-0 mr-8">
        {/* Online Status */}
        <a 
          href="https://status.nordex.trade" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
          title="View system status"
        >
          <div
            className={`h-2 w-2 rounded-full transition-colors duration-300 ${
              isOnline ? "bg-bid-foreground" : "bg-ask-foreground"
            }`}
            aria-label={isOnline ? "Online" : "Offline"}
          />
          <span className="text-muted-foreground">
            {isOnline ? "Online" : "Offline"}
          </span>
        </a>

        {/* Favorites with Arrow Navigation */}
        {favorites.length > 0 && (
          <>
            <div className="h-4 w-px bg-border flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Left Arrow */}
              <button
                onClick={scrollLeft}
                className="flex-shrink-0 p-0.5 hover:text-foreground text-muted-foreground transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeftIcon className="size-3" />
              </button>
              
              {/* Favorites */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-muted-foreground flex-shrink-0">Favorites:</span>
                <div 
                  ref={scrollContainerRef}
                  className="flex items-center gap-3 overflow-x-auto scrollbar-hide"
                >
                  {favoriteMarkets.map((market) => (
                    <FavoriteMarketButton
                      key={market.symbol}
                      market={market}
                      isActive={product?.id === market.symbol}
                      onClick={() => handleMarketClick(market.symbol)}
                    />
                  ))}
                </div>
              </div>
              
              {/* Right Arrow */}
              <button
                onClick={scrollRight}
                className="flex-shrink-0 p-0.5 hover:text-foreground text-muted-foreground transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRightIcon className="size-3" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Right Side - Links (Always Visible, Never Covered) */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <a
          href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}contact`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Support
        </a>
        <a
          href={`${import.meta.env.VITE_DOCS_URL || "/docs/"}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Docs
        </a>
        <a
          href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}terms`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Terms
        </a>
        <a
          href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}privacy`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Privacy
        </a>
      </div>
    </div>
  );
};

